// Dark-aware chart theming for chart.js.
//
// Why not useDarkMode()? That composable returns a *per-instance* `isDark` ref that only
// reacts to its own toggle / system changes — it won't update when the navbar toggles the
// `.dark` class on <html>. Charts are canvas (no CSS), so they need real reactive colors.
// This module keeps a single shared `isDark` ref backed by a MutationObserver on the
// documentElement's class list, so every chart re-themes instantly on toggle.

import { ref, computed } from 'vue'

const hasDom = typeof document !== 'undefined'

const isDark = ref(hasDom && document.documentElement.classList.contains('dark'))

if (hasDom && typeof MutationObserver !== 'undefined') {
  const observer = new MutationObserver(() => {
    isDark.value = document.documentElement.classList.contains('dark')
  })
  observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] })
}

// Display font for chart numbers/labels (matches the .font-display utility).
const FONT_FAMILY = "'Space Grotesk', 'Inter', system-ui, sans-serif"

// Brand palette (hex — chart.js needs real colors, not Tailwind classes).
export const chartColors = {
  primary: '#6B8E23',   // volume (brand green)
  emerald: '#10b981',   // completed
  secondary: '#1E90FF', // orders / collection points (blue)
  amber: '#f59e0b',     // completion rate
  violet: '#8b5cf6',
  red: '#ef4444',
}

// Categorical palette for multi-item charts (zones, group members, locations).
export const seriesPalette = [
  '#6B8E23', '#1E90FF', '#f59e0b', '#10b981', '#8b5cf6',
  '#ec4899', '#14b8a6', '#f97316', '#06b6d4', '#64748b',
]

export function useChartTheme() {
  const axisColor = computed(() => (isDark.value ? '#a3a3a3' : '#737373'))
  const gridColor = computed(() => (isDark.value ? 'rgba(255,255,255,0.07)' : 'rgba(23,23,23,0.07)'))
  const tooltipBg = computed(() => (isDark.value ? 'rgba(38,38,38,0.96)' : 'rgba(23,23,23,0.92)'))
  const tooltipColor = computed(() => (isDark.value ? '#f5f5f5' : '#ffffff'))
  const tooltipBorder = computed(() => (isDark.value ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.1)'))

  // Convenience: a plain object snapshot for the option factories.
  const theme = computed(() => ({
    isDark: isDark.value,
    fontFamily: FONT_FAMILY,
    axisColor: axisColor.value,
    gridColor: gridColor.value,
    tooltipBg: tooltipBg.value,
    tooltipColor: tooltipColor.value,
    tooltipBorder: tooltipBorder.value,
  }))

  return {
    isDark,
    fontFamily: FONT_FAMILY,
    axisColor,
    gridColor,
    tooltipBg,
    tooltipColor,
    tooltipBorder,
    theme,
    chartColors,
    seriesPalette,
  }
}
